import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trophy, Users, Euro } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/app/ui';

/**
 * Stelt het leaderboard een keer voor aan wie er nog nooit over is gevraagd.
 *
 * De keuze wordt opgeslagen als leaderboard_opt_in, en daar is null iets
 * anders dan false: null betekent "nog nooit gevraagd", false betekent "nee
 * gezegd". Zonder dat onderscheid zou iedereen die nee zegt de vraag bij elke
 * volgende sessie opnieuw krijgen.
 *
 * Daarom is er ook geen kruisje en geen klik-naast-om-weg: wegklikken zou de
 * vraag onbeantwoord laten en hem dus eindeloos laten terugkomen. Beide
 * knoppen leggen een echt antwoord vast, en allebei zijn ze later nog te
 * wijzigen bij Instellingen.
 */
export default function LeaderboardIntro() {
  const { axiosAuth, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [bezig, setBezig] = useState(false);
  // Een keer vragen, en na een antwoord nooit meer. axiosAuth() geeft bij elke
  // render een nieuwe functie terug, dus zonder deze vlag vuurde het effect
  // opnieuw af en stond de dialoog er zo weer.
  const alGevraagd = useRef(false);

  useEffect(() => {
    if (!user || alGevraagd.current) return undefined;
    alGevraagd.current = true;
    let afgebroken = false;
    axiosAuth()
      .get('/settings')
      .then((res) => {
        // Alleen bij null: wie al ja of nee zei wordt met rust gelaten.
        if (!afgebroken && res.data?.leaderboard_opt_in === null) setOpen(true);
      })
      .catch(() => {
        // Stil: een mislukte vraag is geen reden om een dialoog te tonen.
      });
    return () => {
      afgebroken = true;
    };
  }, [axiosAuth, user]);

  const antwoord = useCallback(
    async (meedoen, loonOok) => {
      setBezig(true);
      try {
        const payload = { leaderboard_opt_in: meedoen };
        if (meedoen) payload.share_earnings = Boolean(loonOok);
        await axiosAuth().put('/settings', payload);
        alGevraagd.current = true;
        setOpen(false);
        toast.success(
          meedoen ? 'Je doet mee aan het leaderboard' : 'Prima, je blijft onzichtbaar',
        );
      } catch {
        toast.error('Kon je keuze niet opslaan');
      } finally {
        setBezig(false);
      }
    },
    [axiosAuth],
  );

  if (!open) return null;

  return (
    <Dialog open={open}>
      <DialogContent
        /* Het ingebouwde kruisje weg: zonder onOpenChange doet het niets, en
           wegklikken zou de vraag onbeantwoord laten en dus eindeloos laten
           terugkomen. Alleen directe kinderen, dus mijn eigen knoppen blijven. */
        className="max-w-md [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <span className="w-11 h-11 rounded-xl bg-dd-lime/10 border border-dd-lime/25 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-dd-lime" />
          </span>
          <DialogTitle className="mt-3">Er is een leaderboard</DialogTitle>
          <DialogDescription className="leading-relaxed">
            Zie hoe je uren en ritten zich verhouden tot die van je collega's. Meedoen is
            vrijwillig en werkt op wederkerigheid.
          </DialogDescription>
        </DialogHeader>

        <ul className="mt-1 space-y-2.5 text-[13px] text-dd-muted">
          <li className="flex items-start gap-2.5">
            <Users className="w-4 h-4 mt-0.5 flex-shrink-0 text-dd-dim" />
            <span>
              Doe je mee, dan zien andere deelnemers jouw uren en ritten, en zie jij die van
              hen. Doe je niet mee, dan sta je er niet in en zie je hem ook niet.
            </span>
          </li>
          <li className="flex items-start gap-2.5">
            <Euro className="w-4 h-4 mt-0.5 flex-shrink-0 text-dd-dim" />
            <span>
              Je loon staat er standaard <strong className="text-dd-text">niet</strong> bij.
              Dat is een aparte keuze, met dezelfde regel: alleen wie zijn eigen loon deelt,
              ziet dat van anderen.
            </span>
          </li>
        </ul>

        <p className="mt-3 text-[12px] text-dd-dim">
          Je kunt dit later altijd aanpassen bij Instellingen.
        </p>

        <div className="mt-4 flex flex-col gap-2">
          <Button onClick={() => antwoord(true, true)} disabled={bezig}>
            Meedoen, inclusief mijn loon
          </Button>
          <Button variant="secondary" onClick={() => antwoord(true, false)} disabled={bezig}>
            Meedoen, zonder mijn loon
          </Button>
          <Button variant="ghost" onClick={() => antwoord(false, false)} disabled={bezig}>
            Nee, laat mij erbuiten
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
